/**
 * MongoDB implementation of Person Repository
 * 
 * Clean infrastructure implementation
 */
import { MongoClient, Db, Collection } from 'mongodb';
import { Person } from '../../bounded-contexts/parties/domain/aggregates/Person';
import { IPersonRepository } from '../../bounded-contexts/parties/domain/repositories/IPersonRepository';
import { v4 as uuidv4 } from 'uuid';

export class MongoPersonRepository implements IPersonRepository {
  private db: Db;
  private collection: Collection;
  private client: MongoClient;

  constructor(
    private readonly mongoUrl: string,
    private readonly dbName: string
  ) {}

  private async ensureConnection(): Promise<void> {
    if (!this.client) {
      this.client = new MongoClient(this.mongoUrl);
      await this.client.connect();
      this.db = this.client.db(this.dbName);
      this.collection = this.db.collection('persons');
      
      // Create indexes
      await this.collection.createIndex({ email: 1 }, { unique: true });
      await this.collection.createIndex({ organizationId: 1 });
      await this.collection.createIndex({ department: 1 });
      await this.collection.createIndex({ isActive: 1 });
    }
  }

  async save(person: Person): Promise<void> {
    await this.ensureConnection();
    const snapshot = person.toSnapshot();
    
    await this.collection.replaceOne(
      { _id: snapshot.id },
      { _id: snapshot.id, ...snapshot },
      { upsert: true }
    );
  }

  async findById(id: string): Promise<Person | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ _id: id });
    
    if (!doc) return null;
    
    return Person.fromSnapshot(doc);
  }

  async findByEmail(email: string): Promise<Person | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ 
      email: email.toLowerCase() 
    });
    
    if (!doc) return null;
    
    return Person.fromSnapshot(doc);
  }

  async emailExists(email: string): Promise<boolean> {
    await this.ensureConnection();
    const count = await this.collection.countDocuments({ 
      email: email.toLowerCase() 
    });
    
    return count > 0;
  }

  async findByOrganization(organizationId: string): Promise<Person[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      organizationId,
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Person.fromSnapshot(doc));
  }

  async findByDepartment(department: string): Promise<Person[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ 
      department,
      isActive: true 
    }).toArray();
    
    return docs.map(doc => Person.fromSnapshot(doc));
  }

  async findAll(offset: number = 0, limit: number = 100): Promise<Person[]> {
    await this.ensureConnection();
    const docs = await this.collection
      .find({ isActive: true })
      .skip(offset)
      .limit(limit)
      .toArray();
    
    return docs.map(doc => Person.fromSnapshot(doc));
  }

  async count(): Promise<number> {
    await this.ensureConnection();
    return await this.collection.countDocuments({});
  }

  async countActive(): Promise<number> {
    await this.ensureConnection();
    return await this.collection.countDocuments({ isActive: true });
  }

  async remove(id: string): Promise<void> {
    await this.ensureConnection();
    // Soft delete - just mark as inactive
    await this.collection.updateOne(
      { _id: id },
      { $set: { isActive: false, updatedAt: new Date() } }
    );
  }

  nextIdentity(): string {
    return uuidv4();
  }

  async disconnect(): Promise<void> {
    if (this.client) {
      await this.client.close();
    }
  }
}